import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import ResumePreview from '../ResumePreview';
import { TEMPLATES, SAMPLE_RESUME_DATA } from '../templateMeta';

/**
 * Home-page carousel that rotates through every template using the same
 * canonical list as the /select-template page. Clicking a card sends the
 * user to /select-template with that template pre-selected.
 */
function TemplatePreviewCarousel() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const updateVisibleCount = () => {
      if (window.innerWidth < 768) setVisibleCount(1);
      else if (window.innerWidth < 1024) setVisibleCount(2);
      else setVisibleCount(3);
    };
    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  const total = TEMPLATES.length;
  const nextPage = () => setCurrentIndex((prev) => (prev + visibleCount >= total ? 0 : prev + visibleCount));
  const prevPage = () => setCurrentIndex((prev) => (prev - visibleCount < 0 ? Math.max(0, total - visibleCount) : prev - visibleCount));
  const visible = TEMPLATES.slice(currentIndex, currentIndex + visibleCount);
  const pageCount = Math.ceil(total / visibleCount);

  return (
    <div className="w-full">
      {/* Header + nav arrows */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={prevPage}
          className="p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition text-gray-700 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={currentIndex === 0}
          aria-label="Previous templates"
        >
          <FaChevronLeft className="text-xl" />
        </button>
        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-900">Template Previews</h3>
          <p className="text-sm text-gray-600 mt-1">
            {currentIndex + 1}–{Math.min(currentIndex + visibleCount, total)} of {total}
          </p>
        </div>
        <button
          onClick={nextPage}
          className="p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition text-gray-700 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={currentIndex + visibleCount >= total}
          aria-label="Next templates"
        >
          <FaChevronRight className="text-xl" />
        </button>
      </div>

      {/* Cards */}
      <div className={`grid grid-cols-1 ${visibleCount >= 2 ? 'md:grid-cols-2' : ''} ${visibleCount >= 3 ? 'lg:grid-cols-3' : ''} gap-4 sm:gap-6 mb-6`}>
        {visible.map((template) => (
          <button
            key={template.key}
            onClick={() => navigate('/select-template', { state: { selectedTemplate: template.key } })}
            className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition transform hover:-translate-y-1 text-left flex flex-col"
            aria-label={`Preview ${template.label} template`}
          >
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 sm:p-4">
              <h4 className="font-bold text-base sm:text-lg">{template.label}</h4>
              <p className="text-xs text-blue-100 mt-0.5">{template.bestFor}</p>
            </div>

            {/* Scaled-down page preview. Templates render at fixed 8.5" wide
                (816px); we scale to ~32% so it fits the card and clip the
                bottom (overflow:hidden) to show just the top of the resume. */}
            <div className="p-2 sm:p-3 bg-gray-50 flex-grow">
              <div
                className="bg-white rounded-lg shadow-inner overflow-hidden border border-gray-200 mx-auto"
                style={{ height: '380px' }}
              >
                <div
                  className="origin-top-left pointer-events-none"
                  style={{ transform: 'scale(0.36)', transformOrigin: 'top left', width: '278%' }}
                >
                  <ResumePreview data={SAMPLE_RESUME_DATA} template={template.key} />
                </div>
              </div>
            </div>

            <div className="px-3 sm:px-4 py-3 bg-white border-t border-gray-100">
              <p className="text-xs sm:text-sm text-gray-600 leading-snug">{template.description}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Dot indicator */}
      <div className="flex justify-center gap-2">
        {Array.from({ length: pageCount }).map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index * visibleCount)}
            className={`w-3 h-3 rounded-full transition ${
              Math.floor(currentIndex / visibleCount) === index ? 'bg-blue-600 w-8' : 'bg-gray-300 hover:bg-gray-400'
            }`}
            aria-label={`Go to template group ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default TemplatePreviewCarousel;
